/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/**
 * 发放引导页的操作步骤
 */
export enum GuideCreateOptions {
    /**
     * 创建LUN
     */
    CreateLun = 0,
    /**
     * 创建主机
     */
    CreateHost = 1,
    /**
     * 添加启动器
     */
    AddInitiator = 2,
    /**
     * 将创建的LUN映射给主机
     */
    MapLunToHost = 3,
    /**
     * 结束
     */
    Finished = 4
}

/**
 * 智能助手存储在Session/LocalStorage里的key
 */
export enum GuideStorageKey {
    /**
     * 是否显示极简/标准模式切换Tip
     */
    hideSimpleModeTip = 'hideSimpleModeTip',
    /**
     * 是否首次进入极简模式
     */
    firstEnterSimpleMode = 'firstEnterSimpleMode',
    /**
     * 引导配置信息
     */
    guideConfig = 'guideConfig',
    /**
     * 是否显示智能助手侧滑框
     */
    showSmartAssistant = 'showSmartAssistant',
    /**
     * 是否开始引导
     */
    startGuiding = 'startGuiding',
    /**
     * 发放引导步骤
     */
    step = 'step',
    /**
     * 已创建的LUN
     */
    createdLuns = 'createdLuns',
    /**
     * 已创建的主机
     */
    createdHost = 'createdHost',
    /**
     * 已创建的启动器
     */
    createdInitiator = 'createdInitiator',
    /**
     * 已映射的LUN
     */
    mappedLun = 'mappedLun',
    /**
     * 是否是点击“继续创建”按钮
     */
    stepForCreateMore = 'stepForCreateMore',
    /**
     * 用户实际执行到的步骤
     */
    latestSteps = 'latestSteps',
    /**
     * 引导类型
     */
    guideOperateType = 'guideOperateType',
    /**
     * 引导开始时间
     */
    startTime = 'startTime',
    /**
     * 主机是否被删除
     */
    isHostDeleted = 'isHostDeleted',
    /**
     * 判断是否已完成引导
     */
    isFinished = 'isFinished',
    /**
     * 调用智能助手的入口
     */
    entry = 'entry',
    /**
     * 是否显示智能助手日志导出
     */
    showExportLog = 'showExportLog',
    /**
     * 是否显示引导步骤的primary按钮
     */
    showPrimaryBtn = 'showPrimaryBtn'
}

/**
 * 智能助手在EventService里使用的key
 */
export enum GuideEventKey {
    /**
     * 智能助手配置数据变化(core库也在用该事件名, 修改时请同步修改)
     */
    assistantConfigChange = 'assistantConfigChange',
    /**
     * 引导中创建的LUN
     */
    lunCreated = 'lunCreated',
    /**
     * 引导中有LUN被修改
     */
    lunModified = 'lunModified',
    /**
     * 引导中有LUN被删除
     */
    lunDeleted = 'lunDeleted',
    /**
     * 引导中创建的主机
     */
    hostCreated = 'hostCreated',
    /**
     * 引导中创建的启动器
     */
    initiatorCreated = 'initiatorCreated',
    /**
     * 引导中映射的LUN
     */
    mappedLun = 'mappedLun'
}

/**
 * 已创建对象的信息最多显示3条
 */
export const MAX_DISPLAY_COUNT = 3;

/**
 * 主机数量最多1个
 */
export const HOST_DISPLAY_NUM = 1;

/**
 * 轮询时间（30秒）
 */
export const GUIDE_POLLING_TIME = 30000;

/**
 * 调用入口
 */
export enum SmartAssistantEntryE {
    /**
     * 首页
     */
    Home = 'home',
    /**
     * LUN/主机资源发放引导
     */
    Block = 'block',
    /**
     * 告警和事件
     */
    AlarmAndEvent = 'alarmAndEvent',
    /**
     * 性能监控
     */
    PERFORMANCE_MONITOR = 'performanceMonitor',
    /**
     * 文件共享
     */
    FileShare = 'fileShare',
    /**
     * 数据保护
     */
    ObjectProtection = 'objectProtection',
    /**
     * 报告
     */
    Report = 'report',
    /**
     * 存储硬件
     */
    Hardware = 'hardware',
    /**
     * 设置
     */
    Settings = 'settings'
}

/**
 * 路由与智能助手展示内容的映射
 */
export const routerAndEntryMap = {
    '/simple/home': SmartAssistantEntryE.Home,
    '/simple/block/lun': SmartAssistantEntryE.Block,
    '/simple/block/hosts': SmartAssistantEntryE.Block,
    '/simple/file-service': SmartAssistantEntryE.FileShare,
    '/simple/protection': SmartAssistantEntryE.ObjectProtection,
    '/simple/alarms-and-events': SmartAssistantEntryE.AlarmAndEvent,
    '/simple/performance-monitor': SmartAssistantEntryE.PERFORMANCE_MONITOR,
    '/simple/monitor/schedule-report': SmartAssistantEntryE.Report,
    '/simple/system/hardware': SmartAssistantEntryE.Hardware,
    '/simple/settings': SmartAssistantEntryE.Settings,
};

/**
 * 展示助手首页的入口
 */
export const SHOW_HOME_ENTRY = [SmartAssistantEntryE.Home, SmartAssistantEntryE.PERFORMANCE_MONITOR,
SmartAssistantEntryE.Hardware, SmartAssistantEntryE.Settings];

/**
 * 助手各模块显示入口
 */
export const ENTRY_CHECK_LIST = {
    /**
     * 可以看到助手-LUN、主机的入口
     */
    block: [SmartAssistantEntryE.Block, ...SHOW_HOME_ENTRY],
    /**
     * 可以看到助手-文件服务的入口
     */
    fileShare: [SmartAssistantEntryE.FileShare, ...SHOW_HOME_ENTRY],
    /**
     * 可以看到助手-数据保护的入口
     */
    protection: [SmartAssistantEntryE.ObjectProtection, ...SHOW_HOME_ENTRY],
    /**
     * 可以看到助手-告警的入口（告警页要显示日志导出）
     */
    alarms: [SmartAssistantEntryE.AlarmAndEvent, ...SHOW_HOME_ENTRY],
    /**
     * 可以看到助手-报告入口
     */
    report: [SmartAssistantEntryE.Report, ...SHOW_HOME_ENTRY],
};

/**
 * 默认查询10个对象
 */
export const OBJECT_QUERY_RANGE = '[0-10]';

/**
 * 文件共享、数据保护等模块在首页的助手中显示的最大高度（单位px）
 */
export const MAX_HEIGHT = {
    normal: '100%',
    fileShare: 220,
    protection: 240,
    alarm: 280,
};

/**
 * 权限检查对象
 */
export enum PermissionCheckObjE {
    /**
     * LUN、主机、启动器和映射
     */
    LunAndHost = 'lunAndHost',
    /**
     * 文件系统
     */
    Fs = 'fs',
    /**
     * 文件共享
     */
    FileShare = 'fileShare',
    /**
     * 数据保护
     */
    Protection = 'protection',
    /**
     * 告警和事件
     */
    AlarmAndEvent = 'alarmAndEvent',
    /**
     * 报告
     */
    Report = 'report',
    /**
     * 日志
     */
    Log = 'log'
}
